import { useEffect, useState, type ReactNode } from 'react';
import { useAiAccess } from '../../pages/ai/useAiAccess.js';
import { useCard41Access } from '../../pages/admin/card41/useCard41Access.js';
import { Link, useLocation } from 'react-router-dom';
import { Badge, Box, Drawer, Group, ScrollArea, Stack, Text, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useAuth } from '../../auth/AuthContext.js';
import { manualCounterpartiesApi } from '../../api/manual-counterparties.js';
import { NotificationsBell } from '../../pages/NotificationsBell.js';
import { NowBar } from '../NowBar.js';
import { BackHome } from '../BackHome.js';
import { PushButton } from './PushButton.js';
import logoHorizontal from '../../assets/auth/logo-horizontal.svg';
import {
  visibleWorkSections, visibleCatalogSections, visibleSystemSections,
  canSeePendingCounterparties, sectionsOfGroup, GROUP_LABEL,
  type Section, type AdminSection,
} from '../../navigation/sections.js';
import { sectionIcon, adminSectionIcon } from './sectionIcons.js';
import { GridIcon, LogoutIcon, MenuIcon, InboxCheckIcon } from './internalIcons.js';
import {
  ACCENT, ACTIVE_BG, APP_BG, BORDER, DESKTOP_QUERY, MUTED, SIDEBAR_BG, SIDEBAR_HOVER,
  SIDEBAR_GROUP, SIDEBAR_MUTED, SIDEBAR_WIDTH, SURFACE, TEXT, TOPBAR_HEIGHT,
} from '../../theme/tokens.js';

/**
 * Каркас внутреннего портала: тёмный сайдбар + светлая верхняя панель.
 *
 * До редизайна оболочки у сотрудников не было вовсе — в `Protected` рендерилась
 * полоска «BackHome + NowBar», а меню жило единственным списком ссылок на главной.
 * Теперь навигация постоянна, поэтому по разделам можно ходить напрямую.
 *
 * Права: компонент НИЧЕГО не решает сам — состав пунктов приходит из
 * `navigation/sections.ts`, где условия дословно повторяют дореформенные.
 */

/** Активен ли пункт: для «/» — точное совпадение, иначе префикс пути. */
export function isActivePath(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

function NavItem({
  to, label, icon, active, badge, onNavigate,
}: {
  to: string; label: string; icon: ReactNode; active: boolean;
  badge?: number; onNavigate?: () => void;
}) {
  return (
    <UnstyledButton
      component={Link}
      to={to}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        // 34px: у админа 23 пункта, и только при такой высоте строки всё меню
        // помещается на экран 1024px без прокрутки (фидбек Artem 07.08.2026).
        // Для мыши этого достаточно; на тач-устройствах сайдбар — это Drawer,
        // где высота не ограничена и пункты просторнее.
        minHeight: 34, padding: '5px 10px', borderRadius: 8,
        color: active ? '#fff' : SIDEBAR_MUTED,
        // Активный пункт помечается только подложкой (фидбек Artem 07.08.2026:
        // зелёная полоска слева убрана). Подложку взяли контрастнее прежней,
        // раз она осталась единственным признаком.
        background: active ? ACTIVE_BG : 'transparent',
        fontSize: 14, fontWeight: active ? 600 : 500,
      }}
      onMouseEnter={(e) => {
        // Активный пункт при наведении не трогаем: ховер темнее его подложки,
        // и он бы «гас» под курсором.
        if (!active) e.currentTarget.style.background = SIDEBAR_HOVER;
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = 'transparent';
      }}
    >
      <Box style={{ display: 'flex', flexShrink: 0, color: active ? ACCENT : SIDEBAR_MUTED }}>
        {icon}
      </Box>
      {/* Перенос, а не многоточие: «Дебиторская задолженность» в одну строку не
          помещается, а обрезанная подпись мешает узнать раздел. */}
      <Box style={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>
        {label}
      </Box>
      {badge != null && badge > 0 && (
        <Badge size="sm" variant="filled" color="orange" style={{ flexShrink: 0 }}>{badge}</Badge>
      )}
    </UnstyledButton>
  );
}

function NavGroupLabel({ children }: { children: ReactNode }) {
  return (
    <Text
      fz={11.5}
      fw={700}
      c={SIDEBAR_GROUP}
      tt="uppercase"
      style={{ letterSpacing: '0.06em', padding: '0 12px' }}
    >
      {children}
    </Text>
  );
}

/** Содержимое сайдбара — общее для desktop-панели и мобильного Drawer. */
/**
 * Выход из системы. Живёт в правом верхнем углу топбара (фидбек Artem
 * 08.08.2026) — раньше был внизу сайдбара.
 *
 * Иконкой, а не текстом: соседи по ряду (push, колокольчик) — тоже иконки, а
 * место справа ограничено, там уже прячутся по брейкпоинтам email и часы.
 * Подпись доступна через title и aria-label.
 */
function LogoutButton() {
  const { logout } = useAuth();
  return (
    <UnstyledButton
      onClick={logout}
      title="Выйти"
      aria-label="Выйти"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 36, height: 36, borderRadius: 9, color: MUTED, flexShrink: 0,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = APP_BG; e.currentTarget.style.color = TEXT; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = MUTED; }}
    >
      <LogoutIcon size={19} />
    </UnstyledButton>
  );
}

function SidebarContent({
  work, catalogs, system, pendingCount, pathname, onNavigate,
}: {
  work: Section[]; catalogs: AdminSection[]; system: AdminSection[];
  pendingCount: number; pathname: string; onNavigate?: () => void;
}) {
  const navItem = (s: Section) => (
    <NavItem
      key={s.id}
      to={s.to}
      label={s.navLabel ?? s.label}
      icon={sectionIcon(s.id, 18)}
      active={isActivePath(pathname, s.to)}
      {...(onNavigate ? { onNavigate } : {})}
    />
  );
  const adminItem = (s: AdminSection) => (
    <NavItem
      key={s.id}
      to={s.to}
      label={s.label}
      icon={adminSectionIcon(s.id, 18)}
      active={isActivePath(pathname, s.to)}
      {...(onNavigate ? { onNavigate } : {})}
    />
  );
  return (
    <Stack gap={0} style={{ height: '100%' }}>
      <Box px={14} py={11}>
        <img src={logoHorizontal} alt="EURASIA Tobacco Company" style={{ width: 128, display: 'block' }} />
      </Box>

      {/* minHeight:0 обязателен: без него flex-элемент не сжимается ниже своего
          контента, ScrollArea не получает ограниченную высоту и нижние пункты меню
          уезжают за край без возможности проскроллить. */}
      <ScrollArea style={{ flex: 1, minHeight: 0 }} type="hover" scrollbarSize={6}>
        <Stack gap={2} px={10} pb={10}>
          <NavItem
            to="/"
            label="Рабочий стол"
            icon={<GridIcon size={18} />}
            active={isActivePath(pathname, '/')}
            {...(onNavigate ? { onNavigate } : {})}
          />

          {/*
            Новости — без заголовка группы, сразу под «Рабочим столом»: это
            лента для чтения, а не раздел работы (правка Artem 16.09.2026).
          */}
          {sectionsOfGroup(work, 'news').map(navItem)}

          <Box pt={8} pb={2}><NavGroupLabel>{GROUP_LABEL.work}</NavGroupLabel></Box>
          {sectionsOfGroup(work, 'work').map(navItem)}

          {sectionsOfGroup(work, 'analytics').length > 0 && (
            <>
              <Box pt={10} pb={2}><NavGroupLabel>{GROUP_LABEL.analytics}</NavGroupLabel></Box>
              {sectionsOfGroup(work, 'analytics').map(navItem)}
            </>
          )}

          {catalogs.length > 0 && (
            <>
              <Box pt={10} pb={2}><NavGroupLabel>Справочники</NavGroupLabel></Box>
              {catalogs.map(adminItem)}
            </>
          )}

          {/*
            Администрирование — управление доступом и модерация. «Заявки»
            переехали сюда из «Работы»: согласование контрагента это действие
            администратора, а не ежедневная работа менеджера.
          */}
          {(system.length > 0 || pendingCount > 0) && (
            <>
              <Box pt={10} pb={2}><NavGroupLabel>Администрирование</NavGroupLabel></Box>
              {system.map(adminItem)}
              {pendingCount > 0 && (
                <NavItem
                  to="/admin/pending-counterparties"
                  label="Заявки"
                  icon={<InboxCheckIcon size={18} />}
                  active={isActivePath(pathname, '/admin/pending-counterparties')}
                  badge={pendingCount}
                  {...(onNavigate ? { onNavigate } : {})}
                />
              )}
            </>
          )}
        </Stack>
      </ScrollArea>

    </Stack>
  );
}

export function InternalShell({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  const { pathname } = useLocation();
  const isDesktop = useMediaQuery(DESKTOP_QUERY, false, { getInitialValueInEffect: false });
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingCp, setPendingCp] = useState(0);

  const role = state.status === 'authed' ? state.user.role : null;
  const aiEnabled = useAiAccess(role);
  const card41Enabled = useCard41Access(role);

  // Счётчик заявок на контрагентов — согласовывает только админ (поведение из Home.tsx).
  useEffect(() => {
    if (role !== 'admin' && role !== 'genadmin') { setPendingCp(0); return; }
    manualCounterpartiesApi.listPending()
      .then((rows) => setPendingCp(rows.length))
      .catch(() => { /* не критично: пункт просто не появится */ });
  }, [role]);

  // Drawer закрываем на любом переходе (клик по ссылке внутри).
  useEffect(() => { setMenuOpen(false); }, [pathname]);

  if (state.status !== 'authed' || !role) return null;
  const u = state.user;

  const work = visibleWorkSections(role, aiEnabled, card41Enabled);
  const catalogs = visibleCatalogSections(role);
  const system = visibleSystemSections(role);
  const pendingVisible = canSeePendingCounterparties(role, pendingCp) ? pendingCp : 0;

  const sidebar = (
    <SidebarContent
      work={work}
      catalogs={catalogs}
      system={system}
      pendingCount={pendingVisible}
      pathname={pathname}
      {...(isDesktop ? {} : { onNavigate: () => setMenuOpen(false) })}
    />
  );

  return (
    <Box style={{ minHeight: '100vh', background: APP_BG }}>
      {/* Пропуск навигации: клавиатурный пользователь не обязан проходить всё меню. */}
      <UnstyledButton
        component="a"
        href="#main-content"
        style={{
          position: 'absolute', left: -9999, top: 8, zIndex: 300,
          background: SURFACE, color: TEXT, padding: '8px 14px', borderRadius: 8,
          border: `1px solid ${BORDER}`, fontSize: 14,
        }}
        onFocus={(e) => { e.currentTarget.style.left = '8px'; }}
        onBlur={(e) => { e.currentTarget.style.left = '-9999px'; }}
      >
        Перейти к содержимому
      </UnstyledButton>

      {isDesktop && (
        <Box
          component="nav"
          aria-label="Основная навигация"
          style={{
            position: 'fixed', top: 0, left: 0, bottom: 0, width: SIDEBAR_WIDTH,
            background: SIDEBAR_BG, zIndex: 120,
          }}
        >
          {sidebar}
        </Box>
      )}

      {!isDesktop && (
        <Drawer
          opened={menuOpen}
          onClose={() => setMenuOpen(false)}
          size={SIDEBAR_WIDTH}
          padding={0}
          withCloseButton={false}
          styles={{ content: { background: SIDEBAR_BG }, body: { height: '100%', padding: 0 } }}
          aria-label="Основная навигация"
        >
          {sidebar}
        </Drawer>
      )}

      <Box style={{ paddingLeft: isDesktop ? SIDEBAR_WIDTH : 0 }}>
        <Box
          component="header"
          style={{
            position: 'sticky', top: 0, zIndex: 110,
            height: TOPBAR_HEIGHT, background: SURFACE,
            borderBottom: `1px solid ${BORDER}`,
            display: 'flex', alignItems: 'center', gap: 12,
            padding: isDesktop ? '0 28px' : '0 12px',
          }}
        >
          {!isDesktop && (
            <UnstyledButton
              onClick={() => setMenuOpen(true)}
              aria-label="Открыть меню"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 44, height: 44, borderRadius: 10, color: TEXT, flexShrink: 0,
              }}
            >
              <MenuIcon size={22} />
            </UnstyledButton>
          )}

          {/* Возврат на уровень вверх: сайдбар ведёт только в корень раздела, а из
              карточки заказа нужен путь «← К заказам». Компонент сам решает, показываться
              ли (на «/» возвращает null). */}
          <BackHome />

          {/* Подпись «Корпоративный портал ООО ТК Евразия» на десктопе убрана по
              просьбе Artem 17.09.2026; на мобильном короткий заголовок остаётся,
              а пустой Text держит раскладку (flex: 1 отжимает правый блок). */}
          <Text fw={600} fz={15} c={TEXT} style={{ flex: 1, minWidth: 0 }} truncate>
            {isDesktop ? '' : 'ТК Евразия'}
          </Text>

          {/* Чип роли убран по фидбеку Artem 07.08.2026 — пользователь свою роль знает. */}
          <Group gap={10} wrap="nowrap" style={{ flexShrink: 0 }}>
            <PushButton />
            <NotificationsBell />
            <Text fz={13.5} c={MUTED} visibleFrom="md">{u.email}</Text>
            <Box visibleFrom="lg"><NowBar /></Box>
            <LogoutButton />
          </Group>
        </Box>

        <Box
          component="main"
          id="main-content"
          style={{ minHeight: `calc(100vh - ${TOPBAR_HEIGHT}px)` }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
